import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FaArrowLeft } from "react-icons/fa";
import axios from "../../apis/axiosConfig";

const DomainEndpointDetailsPage = () => {
  const { projectId, endpointId } = useParams();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("run_id");
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endpointData, setEndpointData] = useState(null);

  useEffect(() => {
    loadEndpointDetails();
  }, [projectId, endpointId, runId]);

  const loadEndpointDetails = async () => {
    try {
      setLoading(true);

      // Call the API to get endpoint details
      const response = await axios.get(
        `/seo-results/projects/${projectId}/runs/${runId}/domain-endpoint/${endpointId}/`,
      );

      if (response.data.status === "success") {
        setEndpointData(response.data);
      } else {
        setEndpointData(null);
      }

      setLoading(false);
    } catch (err) {
      // console.error('Error loading endpoint details:', err);
      setError("Failed to load endpoint details");
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return isDarkMode ? "bg-green-900/30" : "bg-green-50";
    if (score >= 60) return isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50";
    return isDarkMode ? "bg-red-900/30" : "bg-red-50";
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              Loading details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!endpointData) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors mb-6 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
        >
          <FaArrowLeft
            className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
        <div className="text-center">
          <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
            Endpoint not found
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
          >
            <FaArrowLeft
              className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          </button>
          <div>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {endpointData.icon} {endpointData.name}
            </h1>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Run ID: {runId} | Project ID: {projectId}
            </p>
          </div>
        </div>

        {/* Score Card */}
        <div
          className={`p-6 rounded-lg shadow-lg mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2
                className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Score
              </h2>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Endpoint Performance Rating
              </p>
            </div>
            <div
              className={`text-center p-8 rounded-full ${getScoreBgColor(endpointData.score)}`}
            >
              <div
                className={`text-6xl font-bold ${getScoreColor(endpointData.score)}`}
              >
                {endpointData.score}%
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div
          className={`p-6 rounded-lg shadow-lg ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <h2
            className={`text-2xl font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Details
          </h2>

          {/* Handle if data is an array of results (like SERP data) */}
          {Array.isArray(endpointData.data) ? (
            <div className="space-y-6">
              {endpointData.data.map((result, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-l-4 ${isDarkMode ? "bg-gray-700 border-blue-500" : "bg-gray-50 border-blue-400"}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {Object.entries(result).map(([key, value]) => {
                      // Skip metrics field and nested objects/arrays
                      if (
                        key === "metrics" ||
                        (typeof value === "object" && value !== null)
                      ) {
                        return null;
                      }

                      return (
                        <div key={key}>
                          <p
                            className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {key.replace(/_/g, " ")}
                          </p>
                          <p
                            className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {typeof value === "boolean"
                              ? value
                                ? "Yes"
                                : "No"
                              : value}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Display metrics for this result */}
                  {result.metrics && (
                    <div
                      className={`p-3 rounded-lg mt-3 ${isDarkMode ? "bg-gray-600" : "bg-white"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        Traffic Metrics
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p
                            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Organic Traffic
                          </p>
                          <p
                            className={`text-xl font-bold ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                          >
                            {result.metrics.organic?.traffic?.toFixed(2) || "0"}
                          </p>
                        </div>
                        <div>
                          <p
                            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            Paid Traffic
                          </p>
                          <p
                            className={`text-xl font-bold ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                          >
                            {result.metrics.paid?.traffic?.toFixed(2) || "0"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Handle if data is a single object */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(endpointData.data).map(([key, value]) => {
                // Skip nested objects, arrays, and metrics field
                if (typeof value === "object" && value !== null) {
                  return null;
                }

                // Skip metrics field - we'll display it specially below
                if (key === "metrics") {
                  return null;
                }

                return (
                  <div
                    key={key}
                    className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
                  >
                    <p
                      className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {key.replace(/_/g, " ")}
                    </p>
                    <p
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {typeof value === "boolean"
                        ? value
                          ? "Yes"
                          : "No"
                        : value}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Special formatting for Website Traffic */}
          {(endpointData.name === "Website Traffic" ||
            endpointData.name?.includes("Traffic")) &&
            endpointData.data?.organic && (
              <div className="mt-8">
                <h3
                  className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  📊 Traffic Metrics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Organic Traffic */}
                  <div
                    className={`p-6 rounded-lg border-2 ${isDarkMode ? "bg-green-900/20 border-green-700" : "bg-green-50 border-green-200"}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">🌱</span>
                      <h4
                        className={`text-xl font-bold ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                      >
                        Organic Traffic
                      </h4>
                    </div>
                    <div
                      className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-white"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Traffic Volume
                      </p>
                      <p
                        className={`text-3xl font-bold ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                      >
                        {endpointData.data.organic.traffic?.toFixed(2) || "0"}
                      </p>
                    </div>
                  </div>

                  {/* Paid Traffic */}
                  <div
                    className={`p-6 rounded-lg border-2 ${isDarkMode ? "bg-blue-900/20 border-blue-700" : "bg-blue-50 border-blue-200"}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">💰</span>
                      <h4
                        className={`text-xl font-bold ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                      >
                        Paid Ads
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div
                        className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-white"}`}
                      >
                        <p
                          className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Traffic Volume
                        </p>
                        <p
                          className={`text-2xl font-bold ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                        >
                          {endpointData.data.paid.traffic?.toFixed(2) || "0"}
                        </p>
                      </div>
                      <div
                        className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-white"}`}
                      >
                        <p
                          className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Total Costs
                        </p>
                        <p
                          className={`text-2xl font-bold ${isDarkMode ? "text-yellow-300" : "text-yellow-700"}`}
                        >
                          ${endpointData.data.paid.costs?.toFixed(2) || "0"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div></>
  );
};

export default DomainEndpointDetailsPage;
